import React, { createContext, useContext, useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { Court } from "services";
import API, { sourceFor } from "services/api";

/**
 * The React Context object for court data
 */
const CourtContext = createContext();

/**
 * Convenience hook for accessing court information
 */
const useCourt = () => useContext(CourtContext);

/**
 * Provides Court data to the component hierarchy to which it is applied, and
 * encapsulates API service call logic.
 */
function CourtProvider({ courtId, children }) {
  const [court, setCourt] = useState({
    links: {},
  });
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasError, setError] = useState(false);
  const [finished, setFinished] = useState(false);

  // Get court information and initialize section data on application load
  useEffect(() => {
    Court.get(courtId).then((court) => {
      try {
        const sections = Object.keys(court.links)
          .filter((linkName) => linkName !== "self")
          .reduce(
            (sections, linkName) => ({
              ...sections,
              [linkName]: { isLoading: true },
            }),
            {},
          );
        setSections(sections);

        const isFederal = /federal/i.test(court.courtType);

        setCourt({ ...court, isFederal, _source: court.source });
      } catch (err) {
        Sentry.captureException(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    });
  }, [courtId]);

  Reflect.defineProperty(court, "source", {
    get() {
      return sourceFor(court._source);
    },
  });

  Reflect.defineProperty(court, "entityType", {
    get() {
      return court.courtType === "Federal District Court"
        ? "federalCourt"
        : "stateCourt";
    },
  });

  useEffect(() => {
    setFinished(false);
    let loaded = [];
    const courtLinks = Object.entries(court.links).filter(
      ([name]) => name !== "self",
    );
    const finish = (name) => {
      loaded.push(name);
      const total = courtLinks.length;
      if (loaded.length === total) setFinished(true);
    };
    courtLinks.forEach(async ([name, url]) => {
      try {
        const { data = [] } = await API.get(url);
        setSections((sections) => ({
          ...sections,
          [name]: data,
        }));
      } catch (err) {
        Sentry.captureException(err);
        setSections((sections) => ({
          ...sections,
          [name]: {
            isLoading: false,
            hasError: true,
          },
        }));
      }
      finish(name);
    });
  }, [court.links]);

  return (
    <CourtContext.Provider
      value={{ court, sections, loading, finished, hasError }}
    >
      {children}
    </CourtContext.Provider>
  );
}

export { CourtProvider, useCourt };
